import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { supabase } from '@/integrations/supabase/client';
import { usePermissions } from '@/hooks/usePermissions';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Plus } from 'lucide-react';

type ContatoFormData = {
  nome: string;
  email: string;
  telefone: string;
  cargo: string;
  observacoes: string;
  cliente_id: string;
  local_id: string;
  id_empresa: string;
};

type Cliente = {
  id: string;
  nome: string;
};

type Local = {
  id: string;
  nome: string;
};

type Empresa = {
  id: string;
  razao_social: string;
};

interface ContatoModalProps {
  onContatoCriado?: () => void;
  trigger?: React.ReactNode;
}

export default function ContatoModal({ onContatoCriado, trigger }: ContatoModalProps) {
  const { hasPermission } = usePermissions();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [locais, setLocais] = useState<Local[]>([]);
  const [empresas, setEmpresas] = useState<Empresa[]>([]);

  const canWrite = hasPermission('contatos', 'contatos', 'write');

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<ContatoFormData>();

  useEffect(() => {
    if (open) {
      loadOptions();
    }
  }, [open]);

  const loadOptions = async () => {
    try {
      // Carregar clientes
      // @ts-ignore
      const { data: clientesData } = await (supabase as any)
        .from('clientes')
        .select('id, nome')
        .eq('ativo', true)
        .order('nome');

      if (clientesData) setClientes(clientesData);

      // Carregar locais
      // @ts-ignore
      const { data: locaisData } = await (supabase as any)
        .from('locais')
        .select('id, nome')
        .eq('ativo', true)
        .order('nome');

      if (locaisData) setLocais(locaisData);

      // Carregar empresas
      // @ts-ignore
      const { data: empresasData } = await (supabase as any)
        .from('empresas')
        .select('id, razao_social')
        .eq('ativo', true)
        .order('razao_social');

      if (empresasData) setEmpresas(empresasData);

    } catch (error: any) {
      console.error('Erro ao carregar opções:', error);
    }
  };

  const onSubmit = async (data: ContatoFormData) => {
    if (!canWrite) {
      toast({
        title: "Erro",
        description: "Você não tem permissão para criar contatos.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const contatoData = {
        ...data,
        cliente_id: data.cliente_id || null,
        local_id: data.local_id || null,
        id_empresa: data.id_empresa || null,
        created_by: (await supabase.auth.getUser()).data.user?.id,
      };

      const { error } = await (supabase as any)
        .from('contatos')
        .insert([contatoData]);

      if (error) throw error;

      toast({
        title: "Sucesso",
        description: "Contato criado com sucesso!",
      });

      reset();
      setOpen(false);
      
      if (onContatoCriado) {
        onContatoCriado();
      }

    } catch (error: any) {
      console.error('Erro ao criar contato:', error);
      toast({
        title: "Erro",
        description: "Erro ao criar contato: " + error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (!canWrite) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button>
            <Plus className="mr-2 h-4 w-4" />
            Novo Contato
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Novo Contato</DialogTitle>
          <DialogDescription>
            Cadastre um novo contato no sistema
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="nome">Nome *</Label>
            <Input
              id="nome"
              {...register('nome', { required: 'Nome é obrigatório' })}
              placeholder="Nome completo"
            />
            {errors.nome && (
              <p className="text-sm text-destructive">{errors.nome.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">E-mail</Label>
            <Input
              id="email"
              type="email"
              {...register('email')}
              placeholder="email@exemplo.com"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="telefone">Telefone</Label>
            <Input
              id="telefone"
              {...register('telefone')}
              placeholder="(11) 99999-9999"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="cargo">Cargo</Label>
            <Input
              id="cargo"
              {...register('cargo')}
              placeholder="Ex: Gerente, Diretor..."
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="cliente_id">Cliente (opcional)</Label>
            <Select 
              value={watch('cliente_id') || undefined} 
              onValueChange={(value) => setValue('cliente_id', value || '')}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione um cliente (opcional)" />
              </SelectTrigger>
              <SelectContent>
                {clientes.map((cliente) => (
                  <SelectItem key={cliente.id} value={cliente.id}>
                    {cliente.nome}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="local_id">Local (opcional)</Label>
            <Select 
              value={watch('local_id') || undefined} 
              onValueChange={(value) => setValue('local_id', value || '')}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione um local (opcional)" />
              </SelectTrigger>
              <SelectContent>
                {locais.map((local) => (
                  <SelectItem key={local.id} value={local.id}>
                    {local.nome}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="id_empresa">Empresa (opcional)</Label>
            <Select 
              value={watch('id_empresa') || undefined} 
              onValueChange={(value) => setValue('id_empresa', value || '')}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione uma empresa (opcional)" />
              </SelectTrigger>
              <SelectContent>
                {empresas.map((empresa) => (
                  <SelectItem key={empresa.id} value={empresa.id}>
                    {empresa.razao_social}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="observacoes">Observações</Label>
            <Textarea
              id="observacoes"
              {...register('observacoes')}
              rows={3}
              placeholder="Observações adicionais..."
            />
          </div>

          <div className="flex justify-end space-x-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? "Salvando..." : "Criar Contato"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}